/**
 * Le palier de gamme d'une intervention (lot U4, ADR-0016) : la logique SANS écran.
 *
 * Le palier se choisit intervention par intervention, le moment venu (une tournée n'en fixe aucun). L'écran PROPOSE celui que les
 * heures de la machine désignent (« palier D proposé : prochain point à 72 000 h »), jamais choisi à la place de l'utilisateur :
 * le bouton le pose, rien d'autre. Le choix s'écrit par l'unique chemin de modification (jeton `revision`).
 */
import type { BriefingIntervention, PalierVisite, ProchainEntretien } from '../api/client';
import { propositionDePalier } from './planning-pieces';

type GammeBriefing = NonNullable<BriefingIntervention['gamme']>;

/** Les paliers de la gamme de la machine ; vide sans machine ni gamme. */
export function paliersDuBriefing(gamme: BriefingIntervention['gamme']): readonly PalierVisite[] {
  return gamme?.paliers ?? [];
}

/** Le choix du palier n'a de sens que pour une intervention ouverte, qu'on peut gérer, dont la machine a une gamme à paliers. */
export function choixPalierPossible(peutDeplacer: boolean, gamme: BriefingIntervention['gamme']): boolean {
  return peutDeplacer && paliersDuBriefing(gamme).length > 0;
}

/** Le corps du `PATCH` : le jeton de l'intervention et le palier voulu, `null` pour effacer le choix. */
export function corpsChoixPalier(revision: number, levelKey: string | null): { revision: number; plannedLevelKey: string | null } {
  return { revision, plannedLevelKey: levelKey };
}

/**
 * La proposition d'après les heures de la machine — rien quand le calcul n'a rien à dire, ou quand le palier proposé est
 * DÉJÀ celui qui est choisi (inutile de proposer ce qui est fait).
 */
export function propositionPourIntervention(
  prochain: ProchainEntretien | null,
  paliers: readonly Pick<PalierVisite, 'levelKey' | 'code'>[],
  plannedLevelKey: string | null,
): { readonly levelKey: string; readonly code: string; readonly raison: string } | null {
  const proposition = propositionDePalier(prochain, paliers);
  if (proposition === null || proposition.levelKey === plannedLevelKey) return null;
  return proposition;
}

/** Ce que dit la ligne du palier : le palier choisi et le nombre de pièces, ou ce qu'il reste à faire. */
export function phrasePalierChoisi(gamme: GammeBriefing, plannedLevelKey: string | null): string {
  if (plannedLevelKey === null) return 'Aucun palier choisi : les pièces de la gamme ne sont pas prévues.';
  const palier = gamme.paliers.find((p) => p.levelKey === plannedLevelKey);
  if (!palier) return 'Le palier choisi n’existe plus dans la gamme : choisissez-en un autre.';
  const pieces = palier.pieces === 0 ? 'aucune pièce' : palier.pieces === 1 ? '1 pièce' : `${palier.pieces} pièces`;
  return `Palier ${palier.code} choisi · ${pieces} à remplacer.`;
}

/** Le texte d'une option de la liste : « Palier B — tous les 12 000 h (9 pièces) ». */
export function optionPalier(p: Pick<PalierVisite, 'code' | 'label' | 'pieces'>): string {
  const nom = p.label.trim() === '' ? `Palier ${p.code}` : `Palier ${p.code} — ${p.label.trim()}`;
  const pieces = p.pieces === 0 ? 'aucune pièce' : p.pieces === 1 ? '1 pièce' : `${p.pieces} pièces`;
  return `${nom} (${pieces})`;
}
